import React from 'react'

import { load } from 'scrivito'

import type {
  BotCapabilityKey,
  HostBindings,
} from '../../core/engine/contracts'
import { AiChatBotPanelContent } from '../../ui/AiChatBotPanelContent'
import { useAppDrawerStore } from '@/Atomic/Templates/PushDrawer'
import { FeatureFlags } from '@/Data/const'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { createGenAiClient } from '@/utils/genAiClient'

/**
 * Maps CRM FeatureFlags values → bot capability keys.
 *
 * THIS IS THE ONLY FILE in the bot tree permitted to import @/Data/const FeatureFlags.
 * The Scrivito HostBindings is the seam: CRM-specific flag values stay here; the rest
 * of the bot tree uses BotCapabilityKey strings from engine/contracts.ts only.
 */
const FLAG_TO_CAPABILITY: Readonly<Partial<Record<string, BotCapabilityKey>>> =
  {
    [FeatureFlags.SHOW_CRM_BOT]: 'crmBot',
    [FeatureFlags.SHOW_CONTACT]: 'contacts',
    [FeatureFlags.SHOW_OPPORTUNITY]: 'opportunities',
    [FeatureFlags.SHOW_ORDER]: 'orders',
    [FeatureFlags.SHOW_QUOTE]: 'quotes',
    [FeatureFlags.SHOW_TICKET]: 'tickets',
    [FeatureFlags.SHOW_ACTIVITY]: 'activities',
    [FeatureFlags.SHOW_LEAD]: 'leads',
    [FeatureFlags.SHOW_DOCUMENT]: 'documents',
    [FeatureFlags.SHOW_LIST]: 'segments',
    [FeatureFlags.SHOW_TIME_TRACKING]: 'timeTracking',
    [FeatureFlags.SHOW_ADVANCED_SEARCH]: 'advancedSearch',
  }

/**
 * Translates an array of CRM feature flag strings into a set of bot capability keys.
 * Unknown flag strings are silently ignored — only flags in FLAG_TO_CAPABILITY are mapped.
 */
export function mapFlagsToCapabilities(flags: string[]): Set<string> {
  const result = new Set<string>()
  for (const flag of flags) {
    const capability = FLAG_TO_CAPABILITY[flag]
    if (capability !== undefined) {
      result.add(capability)
    }
  }
  return result
}

/**
 * Builds a Scrivito HostBindings object for the given capability set.
 *
 * NOTE: currentUserId still resolves directly via Scrivito load in slice 1a.
 * Slice 1b will route this through DataAccessAdapter.get instead.
 *
 * warning: the `as string` cast on WhoAmI.get mirrors the existing session-context.ts
 * pattern; Scrivito's load type returns unknown and there is no narrower overload.
 * Slice 1b will replace this with a properly-typed DataAccessAdapter call.
 */
export function createScrivitoHostBindings(
  capabilities: Set<string>,
): HostBindings {
  return {
    capabilities,
    currentUserId: async (): Promise<string> =>
      ((await load(() => WhoAmI.get('contactId'))) ?? '') as string,
    createLlmClient: createGenAiClient,
    presentPanel: (onClose: () => void): boolean => {
      const drawerStore = useAppDrawerStore.getState()
      if (drawerStore.isOccupied) return false
      // onClose from the shell is drawerStore.close — the store passes its own
      // close() so the conversation stops and isOpen resets on the X button too.
      drawerStore.open((_onClose) =>
        React.createElement(AiChatBotPanelContent, { onClose }),
      )
      return true
    },
    dismissPanel: (): void => {
      useAppDrawerStore.getState().close()
    },
  }
}
